import React, { useCallback, useState, useEffect } from 'react';
import * as Yup from 'yup';
import Form from '../../../../../components/Form';
import { Avatar, Button, Input, Select, Tag } from 'antd';
import { useCreateProgram } from '../../../../../contexts/CreateProgram';
import { ButtonsContainer, CasterOptContainer } from '../styles';
import { FiArrowRight } from 'react-icons/fi';
import { resolveFileSrc } from '../../../../../helpers/fileSrcResolver';
import env from '../../../../../config/env';
import AiModelsAPI from '../../../../../services/sdks/ai-models';

const RuleInput = ({ ruleKey, label, accountInfos, handleChangeAccountInfo, placeholderText }) => {
	const [inputValue, setInputValue] = React.useState('');

	const handleAddRule = (e) => {
		e.preventDefault();
		const value = inputValue.trim();
		if (!value) return;

		const updated = {
			...accountInfos.rules,
			[ruleKey]: [...(accountInfos.rules?.[ruleKey] || []), value],
		};

		handleChangeAccountInfo({
			target: { name: 'rules', value: updated },
		});

		setInputValue('');
	};

	const handleRemoveRule = (ruleToRemove) => {
		const updated = {
			...accountInfos.rules,
			[ruleKey]: accountInfos.rules[ruleKey].filter((rule) => rule !== ruleToRemove),
		};

		handleChangeAccountInfo({
			target: { name: 'rules', value: updated },
		});
	};

	return (
		<Form.Item
			label={label}
			labelCol={{ span: 24 }}
			wrapperCol={{ span: 24 }}
			style={{ marginBottom: 0 }}>
			<>
				<Input.TextArea
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					placeholder={placeholderText}
					autoSize={{ minRows: 1, maxRows: 3 }}
					onPressEnter={handleAddRule}
				/>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 8,
						marginTop: 8,
					}}>
					{(accountInfos.rules?.[ruleKey] || []).map((rule, index) => (
						<Tag
							key={`${ruleKey}-${rule}-${index}`}
							closable
							onClose={(e) => {
								e.preventDefault();
								handleRemoveRule(rule);
							}}
							style={{
								whiteSpace: 'normal',
								lineHeight: 1.5,
								padding: '8px 12px',
								fontSize: 14,
								background: '#fafafa',
								border: '1px solid #d9d9d9',
							}}>
							{rule}
						</Tag>
					))}
				</div>
			</>
		</Form.Item>
	);
};

export const ProgramInfosStep = ({ genres, casters, nextStep, user }) => {
	const [errors, setErrors] = useState({
		name: null,
		description: null,
		audience: null,
		theme: null,
		filenameTemplate: null,
		genreId: null,
		dayPeriod: null,
		segments: null,
		personality: null,
		accent: null,
		regionalCharacteristics: null,
		rules: null,
	});

	const [aiModels, setAiModels] = useState([]);

	const getAiModels = async (userId) => {
		const { data } = await AiModelsAPI.listAiModels({ userId, pageSize: 9999 });
		const { total, aiModels } = data;
		if (total) {
			setAiModels(
				aiModels.map((aiModel) => {
					return { value: aiModel._id, label: aiModel.title };
				})
			);
		} else {
			setAiModels([]);
		}
	};

	useEffect(() => {
		getAiModels(user._id);
	}, [user]);

	const { handleChangeInfo, infos, setInfos } = useCreateProgram();

	const onNextStep = useCallback(async () => {
		const validationSchema = Yup.object().shape({
			name: Yup.string().required(),
			description: Yup.string().required(),
			audience: Yup.string().required(),
			theme: Yup.string().required(),
			filenameTemplate: Yup.string().required(),
			genreId: Yup.string().required(),
			dayPeriod: Yup.string()
				.nullable()
				.notRequired()
				.oneOf(
					[
						'MORNING',
						'LATE_MORNING',
						'AFTERNOON',
						'LATE_AFTERNOON',
						'NIGHT',
						'LATE_NIGHT',
						'DAWN',
						null,
					],
					'dayPeriod inválido'
				),
		});

		try {
			setErrors({});
			await validationSchema.validate(infos, { abortEarly: false });
			nextStep();
		} catch (error) {
			setErrors(
				Object.values(error.inner).reduce((err, { path }) => ({ ...err, [path]: true }), {})
			);
		}
	}, [infos, nextStep]);

	return (
		<>
			<Form.Container layout='1fr 1fr 1fr 1fr 1fr 1fr'>
				<Form.Item size={2} label='Nome' error={errors?.name}>
					<Input
						name='name'
						value={infos.name}
						size='large'
						placeholder='Nome do programa'
						onChange={handleChangeInfo}
					/>
				</Form.Item>
				<Form.Item size={2} label='Público Alvo' error={errors?.audience}>
					<Input
						name='audience'
						value={infos.audience}
						size='large'
						placeholder='Ex: Jovens adultos entre 18 e 35 anos'
						onChange={handleChangeInfo}
					/>
				</Form.Item>
				<Form.Item size={2} label='Tema do Programa' error={errors?.theme}>
					<Input
						name='theme'
						value={infos.theme}
						size='large'
						placeholder='Ex: Entretenimento e música pop'
						onChange={handleChangeInfo}
					/>
				</Form.Item>
				<Form.Item
					size={3}
					label='Padrão do Nome dos Arquivos'
					help='Nome padrão atribuído aos arquivos dos blocos baixados após a geração dos episódios'
					error={errors?.filenameTemplate}>
					<Input
						name='filenameTemplate'
						value={infos.filenameTemplate}
						size='large'
						placeholder='Padrão do nome dos arquivos'
						onChange={handleChangeInfo}
					/>
				</Form.Item>
				<Form.Item size={3} label='Gênero' error={errors?.genreId}>
					<Select
						showSearch
						size='large'
						value={infos.genreId}
						onChange={(value) => handleChangeInfo({ target: { name: 'genreId', value } })}
						placeholder='Selecione o gênero'
						filterOption={(input, { props: { children } }) => {
							return children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
						}}>
						{genres.map((genre) => (
							<Select.Option key={genre._id} value={genre._id}>
								{genre.name.toUpperCase()}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item size={6} label='Período do Dia'>
					<Select
						allowClear
						size='large'
						placeholder='Selecione o período do dia do programa (opcional)'
						value={infos?.dayPeriod}
						onChange={(value) => handleChangeInfo({ target: { name: 'dayPeriod', value } })}>
						<Select.Option value='MORNING'>Manhã</Select.Option>
						<Select.Option value='LATE_MORNING'>Final da Manhã</Select.Option>
						<Select.Option value='AFTERNOON'>Tarde</Select.Option>
						<Select.Option value='LATE_AFTERNOON'>Final da Tarde</Select.Option>
						<Select.Option value='NIGHT'>Noite</Select.Option>
						<Select.Option value='LATE_NIGHT'>Final da Noite</Select.Option>
						<Select.Option value='DAWN'>Madrugada</Select.Option>
					</Select>
				</Form.Item>
				<Form.Item optional error={errors.withLiveContent} size={6} label='Segmentos'>
					<Select
						mode='multiple'
						size='large'
						name='segments'
						placeholder='Selecione um ou mais segmentos'
						value={infos.segments || []}
						onChange={(value) =>
							handleChangeInfo({
								target: { name: 'segments', value },
							})
						}>
						<Select.Option key='POPULAR' value='POPULAR'>
							Popular
						</Select.Option>
						<Select.Option key='JOVEM' value='JOVEM'>
							Jovem
						</Select.Option>
						<Select.Option key='RELIGIOSO' value='RELIGIOSO'>
							Religioso
						</Select.Option>
						<Select.Option key='NEWS' value='NEWS'>
							News / Jornalismo
						</Select.Option>
						<Select.Option key='ESPORTIVA' value='ESPORTIVA'>
							Esportiva
						</Select.Option>
						<Select.Option key='EDUCATIVA' value='EDUCATIVA'>
							Educativa
						</Select.Option>
						<Select.Option key='UNIVERSITÁRIA' value='UNIVERSITÁRIA'>
							Universitária
						</Select.Option>
						<Select.Option key='COMUNITÁRIA' value='COMUNITÁRIA'>
							Comunitária
						</Select.Option>
						<Select.Option key='RETRO' value='RETRO'>
							Retro
						</Select.Option>
						<Select.Option key='REGIONAL' value='REGIONAL'>
							Regional
						</Select.Option>
						<Select.Option key='VARIEDADES' value='VARIEDADES'>
							Variedades
						</Select.Option>
						<Select.Option key='CORPORATIVA' value='CORPORATIVA'>
							Corporativa
						</Select.Option>
						<Select.Option key='GOVERNAMENTAL' value='GOVERNAMENTAL'>
							Governamental
						</Select.Option>
						<Select.Option key='INFANTIL' value='INFANTIL'>
							Infantil
						</Select.Option>
						<Select.Option key='HUMOR' value='HUMOR'>
							Humor
						</Select.Option>
						<Select.Option key='ENTRETENIMENTO' value='ENTRETENIMENTO'>
							Entretenimento
						</Select.Option>
						<Select.Option key='CULTURAL' value='CULTURAL'>
							Cultural
						</Select.Option>
						<Select.Option key='TALK' value='TALK'>
							Talk
						</Select.Option>
					</Select>
				</Form.Item>

				<Form.Item optional label='Personalidade' size={6}>
					<Input.TextArea
						name='personality'
						value={infos.personality || ''}
						onChange={handleChangeInfo}
						rows={2}
						placeholder='Descreva a Personalidade'
					/>
				</Form.Item>

				<Form.Item optional label='Sotaque' size={6}>
					<Input.TextArea
						name='accent'
						value={infos.accent || ''}
						onChange={handleChangeInfo}
						rows={2}
						placeholder='Descreva o Sotaque'
					/>
				</Form.Item>

				<Form.Item optional label='Características Regionais' size={6}>
					<Input.TextArea
						name='regionalCharacteristics'
						value={infos.regionalCharacteristics || ''}
						onChange={handleChangeInfo}
						rows={4}
						placeholder='Descreva as características regionais'
					/>
				</Form.Item>

				<Form.Item optional label='Regras' style={{ marginBottom: 0 }} size={6}>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
						{[
							{
								key: 'mandatory',
								label: 'Obrigatórias',
								placeholderText: 'Digite uma regra obrigatória e pressione Enter',
							},
							{
								key: 'forbidden',
								label: 'Proibidas',
								placeholderText: 'Digite uma regra proibida e pressione Enter',
							},
							{
								key: 'recommended',
								label: 'Recomendadas',
								placeholderText: 'Digite uma regra recomendada e pressione Enter',
							},
						].map(({ key, label, placeholderText }) => (
							<RuleInput
								key={key}
								ruleKey={key}
								label={label}
								placeholderText={placeholderText}
								accountInfos={infos}
								handleChangeAccountInfo={handleChangeInfo}
							/>
						))}
					</div>
				</Form.Item>

				<Form.Item size={6} label='Modelos de IA'>
					<Select
						mode='multiple'
						allowClear
						showSearch
						size='large'
						style={{ width: '100%' }}
						placeholder='Digite para buscar ou selecione'
						onChange={(aiModelIds) => setInfos((prev) => ({ ...prev, aiModelIds }))}
						value={infos.aiModelIds}
						// loading={aiModels.length === 0}
						disabled={aiModels.length === 0}
						notFoundContent={aiModels.length === 0 ? 'Carregando...' : 'Nenhuma opção encontrada'}
						filterOption={(input, option) =>
							option?.props?.children?.toLowerCase()?.includes(input.toLowerCase()) ?? false
						}>
						{aiModels.map((aiModel) => (
							<Select.Option key={aiModel.value} value={aiModel.value}>
								{aiModel.label}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item size={6} label='Locutor' optional style={{ marginTop: 12 }}>
					<Select
						showSearch
						size='large'
						value={infos.caster}
						onChange={(value) => handleChangeInfo({ target: { name: 'caster', value } })}
						placeholder='Selecione o locutor do programa'
						filterOption={(input, { props: { children } }) => {
							return children.props._search.toLowerCase().indexOf(input.toLowerCase()) >= 0;
						}}>
						{casters.map((caster) => (
							<Select.Option
								key={caster._id}
								value={caster._id}
								_search={`${caster.name} ${caster.surname}`}>
								<CasterOptContainer>
									<Avatar
										size='large'
										src={resolveFileSrc({
											fileName: caster.profilePic,
											folder: ['profile_pics'],
											bucket: env.miscS3Bucket,
										})}
									/>
									<span>
										{caster.name} {caster.surname}
									</span>
								</CasterOptContainer>
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item size={6} label='Descrição do programa' error={errors?.description}>
					<Input
						name='description'
						value={infos.description}
						size='large'
						placeholder='Descreva de forma clara sobre o que se trata o programa, tipo de música, horário que vai ao ar, forma de locução, etc...'
						onChange={handleChangeInfo}
					/>
				</Form.Item>
			</Form.Container>

			<ButtonsContainer>
				<Button type='primary' size='large' onClick={onNextStep}>
					<FiArrowRight />
					Continuar
				</Button>
			</ButtonsContainer>
		</>
	);
};
